import React, { useRef, useState, useEffect } from 'react';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { useT } from '../../i18n';
import './ScrollList.css';

/**
 * Wraps a scrollable list and shows a floating down-chevron (same pattern as the
 * time-slots) whenever there is more content below — so on desktop the user knows
 * the list can be scrolled. The chevron hides once scrolled to the bottom.
 */
export default function ScrollList({ children, active = true }) {
  const t = useT();
  const ref = useRef(null);
  const [showFade, setShowFade] = useState(false);

  const check = () => {
    const el = ref.current;
    if (el) setShowFade(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  };

  useEffect(() => {
    const id = requestAnimationFrame(check);
    window.addEventListener('resize', check);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', check);
    };
  }, [children]);

  /* ★ מסמנים על ה-body שכפתור-הגלילה הצף מוצג כרגע. הכפתור תלוי 56px **מתחת**
     לתיבת-הרשימה (‏`.scroll-list-btn{bottom:-56px}`), ולכן הפוטר שמתחתיו צריך
     ריווח נוסף כדי לא להתחבא מאחוריו — אבל רק כשהכפתור קיים, אחרת נפער חלל
     ריק. CSS אינו יכול "לראות" את הכפתור מבחוץ, ומכאן הקלאס.
     ⚠️ מנוקה ב-unmount כדי שלא יישאר דלוק.

     ★★★ ‏`active` הוא **תיקון של הנחה שהתיישנה** (דיווח-משתמש 04/08).
     ההערה המקורית כאן אמרה "החלפת-טאב מפרקת את הרשימה" — וזה היה נכון כשכל
     כרטיסייה הייתה `TabPanel` נפרד. מאז F1 שני הפאנלים חיים **יחד** ב-`.tab-track`
     ואינם מפורקים בהחלפה, ולכן ה-ScrollList של הכרטיסייה שמחוץ למסך המשיך
     להדליק קלאס **גלובלי**. התוצאה: כרטיסייה ריקה (שמרנדרת `.empty-state` בלי
     ScrollList כלל) קיבלה 116px ריווח עבור כפתור ששייך לכרטיסייה השנייה
     ויושב מחוץ למסך — והפוטר נדחף מתחת לקיפול. נמדד: תחתית הפוטר ב-898px
     במסך של 844px; בלי הריווח היא ב-802px, כלומר נראית.
     ⇒ רק הפאנל **שנמצא על המסך** רשאי לתבוע את הקלאס. */
  useEffect(() => {
    /* ★★★ **פאנל שאינו על המסך אינו נוגע במצב הגלובלי.**
       ⚠️ מאז F1 שני הפאנלים מותקנים יחד, ולכן ה-effect רץ **בשניהם**.
          הפאנל הלא-פעיל רץ עם `on=false` ומחק את `--ans-footer-push`
          שהפאנל הפעיל בדיוק הגדיר — ואז ה-CSS נפל ל-116px הקבועים,
          וזה בדיוק התסמין: תקין ב"תורים שעברו" ושבור ב"תורים קרובים",
          תלוי מי מהם רץ אחרון.
       ★ זו אותה משפחה של הבאג מ-04/08 (קלאס גלובלי שנתבע ע"י פאנל
         מחוץ למסך) — הפעם על משתנה-CSS במקום על קלאס.
       ⇒ יוצאים מוקדם: רק הפאנל הפעיל כותב או מוחק. */
    if (!active) { return undefined; }

    const on = showFade;
    document.body.classList.toggle('ans-has-scroll-chevron', on);

    /* ★★★ הדחיפה **נמדדת** ולא קבועה.
       ⚠️ הבאג (05/08, נצפה בטלפון): ריווח קבוע של 116px הספיק ב"תורים
          שעברו" — שם הכרטיסים גבוהים יותר (שורת-דירוג) והפוטר נדחף מטה
          ממילא — אבל **לא** ב"תורים קרובים", שם הרשימה נמוכה יותר ושורת
          הזכויות נשארה על המסך **מתחת לכפתור הצף**, כשהכפתור מצויר עליה.
          כלומר אותו מספר נתן שתי תוצאות שונות, כי הוא התעלם מגובה התוכן.
       ⇒ מחשבים כמה מקום **באמת** נשאר בין תחתית-הרשימה לתחתית-המסך,
         ודוחפים בדיוק את זה. התוצאה זהה בשני הטאבים ובכל גודל-מסך:
         כשיש כפתור — הפוטר מתחיל מתחת לקיפול ומגיעים אליו בגלילה.
       ★ אין כפתור ⇒ המשתנה מתאפס, ואין דחיפה בכלל. */
    if (on && ref.current) {
      const r = ref.current.getBoundingClientRect();
      // 56px = כמה שהכפתור תלוי מתחת לתיבה, + מרווח נשימה קטן.
      const push = Math.max(0, Math.round(window.innerHeight - r.bottom)) + 72;
      document.body.style.setProperty('--ans-footer-push', push + 'px');
    } else {
      document.body.style.removeProperty('--ans-footer-push');
    }

    return () => {
      document.body.classList.remove('ans-has-scroll-chevron');
      document.body.style.removeProperty('--ans-footer-push');
    };
  }, [active, showFade, children]);

  const scrollDown = () => {
    const el = ref.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    el.scrollTo({
      top: atBottom ? 0 : el.scrollTop + el.clientHeight * 0.8,
      behavior: 'smooth',
    });
  };

  return (
    <div className="scroll-list">
      <div className="scroll-list-viewport" ref={ref} onScroll={check}>
        {children}
      </div>
      {showFade && (
        <>
          <div className="scroll-list-fade" />
          <button
            type="button"
            className="scroll-list-btn"
            onClick={scrollDown}
            aria-label={t('appointments.scrollMore')}
          >
            <KeyboardArrowDownRoundedIcon />
          </button>
        </>
      )}
    </div>
  );
}
